import { useEffect, useState } from 'react';
import { publishersManageService } from '../../../api/publishersManageService';
import { useBulkPublisherActions } from '../../../hooks/useBulkPublisherActions';
import type { BulkPublisherOperationResultDto } from '../../../types/managePublishers';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
  open: boolean;
  publisherIds: string[];
  isActive: boolean;          // true = activating, false = deactivating
  onClose: () => void;
  onCompleted: (result: BulkPublisherOperationResultDto) => void;
}

export default function PublisherStatusChangeDialog({
  open, publisherIds, isActive, onClose, onCompleted,
}: Props) {
  const { t } = useTranslation();
  const [alsoUpdateBooks, setAlsoUpdateBooks] = useState(true);
  const [bookCount, setBookCount] = useState<number | null>(null);
  const { bulkSetActive } = useBulkPublisherActions();

  useEffect(() => {
    if (!open) return;
    setAlsoUpdateBooks(true);
    setBookCount(null);
    publishersManageService.affectedBookCount(publisherIds)
      .then(setBookCount)
      .catch(() => setBookCount(null));
  }, [open, publisherIds]);

  if (!open) return null;

  const submit = async () => {
    const result = await bulkSetActive.mutateAsync({
      publisherIds,
      isActive,
      alsoUpdateBooks,
    });
    onCompleted(result);
    onClose();
  };

  const titleKey = isActive
    ? 'App.Admin.Publishers.PublisherStatusChangeDialog.TitleActivate'
    : 'App.Admin.Publishers.PublisherStatusChangeDialog.TitleDeactivate';

  const statusText = isActive
    ? t('App.Admin.Publishers.PublisherStatusChangeDialog.WillBeSetToActive')
    : t('App.Admin.Publishers.PublisherStatusChangeDialog.WillBeSetToInactive');

  const alsoBooksLabel = isActive
    ? t('App.Admin.Publishers.PublisherStatusChangeDialog.AlsoBooksPublished')
    : t('App.Admin.Publishers.PublisherStatusChangeDialog.AlsoBooksArchived');

  const actionLabel = isActive
    ? t('App.Admin.Publishers.PublisherStatusChangeDialog.Activate')
    : t('App.Admin.Publishers.PublisherStatusChangeDialog.Deactivate');

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-5">
        <h2 className="text-lg font-semibold mb-2">
          {t(titleKey, { count: publisherIds.length })}
        </h2>
        <p className="text-sm text-neutral-700 mb-4">
          {statusText}
        </p>

        <label className="flex items-start gap-2 text-sm mb-4">
          <input
            type="checkbox"
            checked={alsoUpdateBooks}
            onChange={(e) => setAlsoUpdateBooks(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            {alsoBooksLabel}
            {bookCount !== null && <span className="text-neutral-500"> {t('App.Admin.Publishers.PublisherStatusChangeDialog.AffectedBooksCount', { count: bookCount })}</span>}
          </span>
        </label>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1 border border-neutral-300 rounded hover:bg-neutral-100">{t('App.Admin.Publishers.PublisherStatusChangeDialog.Cancel')}</button>
          <button
            type="button"
            onClick={submit}
            disabled={bulkSetActive.isPending}
            className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
          >
            {actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
